import { useQuery } from "@tanstack/react-query";
import { Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { TableHeaderType, TableRowItem } from "@/components/tables/types";
import { RelativeComponentLoader } from "@/components/loader";
import { getActiveContracts, getContractsSummary } from "@/helpers/apis";
import { ContractItem } from "./types";
import dayjs from "dayjs";
import FlexListSummary from "@/components/listSummary/FlexListSummary";
import { useAppState } from "@/modules/app/useAppContext";
import { formatNumber } from "@uicore";
import { useBreadcrumb } from "@/components/breadcrumbs";

const ActiveContractsTable = ({ items }: { items: ContractItem[] }) => {
  const { currency } = useAppState();
  const header: TableHeaderType[] = [
    {
      id: 1,
      uid: "contract_number",
      label: "Contract #",
      render: (i: TableRowItem) => (i as ContractItem).contract_number ?? "-",
    },
    {
      id: 2,
      uid: "contract_item",
      label: "Item",
      render: (i: TableRowItem) => (i as ContractItem).contract_item,
    },
    {
      id: 3,
      uid: "currency",
      label: "Currency",
      render: (i: TableRowItem) => (i as ContractItem).currency,
    },
    {
      id: 4,
      uid: "amount",
      label: "Amount",
      render: (i: TableRowItem) => formatNumber(Number((i as ContractItem).amount), { currency }),
    },
    {
      id: 5,
      uid: "start_date",
      label: "Start",
      render: (i: TableRowItem) => dayjs((i as ContractItem).start_date).format("YYYY-MM-DD"),
    },
    {
      id: 6,
      uid: "end_date",
      label: "End",
      render: (i: TableRowItem) => dayjs((i as ContractItem).end_date).format("YYYY-MM-DD"),
    },
  ];

  return <DataTable header={header} data={{ items }} />;
};

const ContractsDashboard = () => {
  useBreadcrumb("Contracts");
  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["contracts-summary"],
    queryFn: () => getContractsSummary(),
  });
  const { data: active, isLoading: isActiveLoading } = useQuery({
    queryKey: ["contracts-active"],
    queryFn: () => getActiveContracts(),
  });

  if (isSummaryLoading || isActiveLoading)
    return <RelativeComponentLoader label="Loading contracts..." />;

  const { currency } = useAppState();

  // Get min start date and max end date from active contracts
  const getContractTimeperiod = (contracts: ContractItem[]) => {
    if (!contracts || contracts.length === 0) return null;

    const startDates = contracts.map(c => dayjs(c.start_date));
    const endDates = contracts.map(c => dayjs(c.end_date));

    const minStart = startDates.reduce((min, current) => (current.isBefore(min) ? current : min));
    const maxEnd = endDates.reduce((max, current) => (current.isAfter(max) ? current : max));

    return {
      start: minStart.format("MMM DD, YYYY"),
      end: maxEnd.format("MMM DD, YYYY"),
    };
  };

  const timeperiod = getContractTimeperiod(active || []);

  return (
    <div className="al-p-6 al-space-y-6">
      <Stack justify={"between"} align={"center"}>
        <Typography variant="page-heading">Contracts</Typography>
        {timeperiod && (
          <div className="al-text-right">
            <div className="al-text-sm al-text-gray-600">Active Contract Timeperiod</div>
            <div className="al-font-medium">
              {timeperiod.start} - {timeperiod.end}
            </div>
          </div>
        )}
      </Stack>
      {summary ? (
        <FlexListSummary
          summary={[
            {
              label: "Total Contract Amount",
              value: formatNumber(summary.total_amount, { currency }),
            },
            {
              label: "Total Spend Till Now",
              value: formatNumber(summary.total_spend_till_now, { currency }),
            },
            {
              label: "Estimated Total Spend",
              value: formatNumber(summary.estimated_total_spend, { currency }),
            },
            {
              label: "Average Daily Spend",
              value: formatNumber(summary.average_daily_spend, { currency }),
            },
          ]}
        />
      ) : null}
      <Stack direction={"column"} className="p-3 al-bg-white al-rounded-md">
        <Stack direction={"row"} justify={"between"}>
          <h3 className={"al-text-xl al-font-medium"}>Active Contracts</h3>
        </Stack>
        <ActiveContractsTable items={active || []} />
      </Stack>
    </div>
  );
};

export default ContractsDashboard;
